import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import CloseIcon from '@mui/icons-material/Close';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import ClosedCaptionDisabledIcon from '@mui/icons-material/ClosedCaptionDisabled';
import DeleteIcon from '@mui/icons-material/Delete';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import ImageIcon from '@mui/icons-material/Image';
import LastPageIcon from '@mui/icons-material/LastPage';
import PushPinIcon from '@mui/icons-material/PushPin';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    MenuItem,
    Select,
    Slider,
    Tooltip,
    Typography,
} from '@mui/material';
import { useRef } from 'react';
import styled from 'styled-components';
import { useEditor } from './EditorContext';
import { getOutputDuration } from './editorUtils';

/* ── Styles ─────────────────────────────────────────────────────── */

const PanelRoot = styled.div`
    width: 200px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${(p) => (p.$dark ? '#111120' : '#eef0fa')};
    overflow-y: auto;
    overflow-x: hidden;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px 4px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${(p) => (p.$dark ? '#888' : '#666')};
    flex-shrink: 0;
`;

const DropZone = styled.div`
    margin: 4px 8px;
    border: 1px dashed ${(p) => (p.$active ? '#64b5f6' : p.$dark ? '#3a3a50' : '#aab')};
    border-radius: 6px;
    padding: 10px 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    background: ${(p) => (p.$active ? 'rgba(100,181,246,0.08)' : 'transparent')};
    transition: border-color 0.15s, background 0.15s;
    &:hover {
        border-color: #64b5f6;
        background: rgba(100, 181, 246, 0.05);
    }
`;

const TrackCard = styled.div`
    margin: 4px 8px;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${(p) => (p.$dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)')};
    border: 1px solid ${(p) => (p.$dark ? '#2a2a3a' : '#c8cce0')};
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const TrackRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

/* ── Component ───────────────────────────────────────────────────── */

/**
 * MediaLibraryPanel — slim left panel for adding music (BGM) and logo overlay.
 */
export default function MediaLibraryPanel({ darkMode, onClose }) {
    const {
        state,
        addAudioTrack,
        removeAudioTrack,
        updateAudioTrack,
        pinAudioVolume,
        normalizeAudio,
        addLogoTrack,
        removeLogoTrack,
        updateLogoTrack,
        setVideoOffset,
        setVideoEndOffset,
        setPreviewLang,
        toggleSubs,
        hiddenTimelineLangs,
        toggleTimelineLang,
    } = useEditor();
    const { audioTracks, logoTracks, duration, videoStartOffset, videoEndOffset, cuts, previewLang, previewSubsOn, subtitlesByLanguage } = state;
    const langKeys = Object.keys(subtitlesByLanguage || {});
    const outputDuration = getOutputDuration(duration, cuts);
    const totalDuration = (videoStartOffset || 0) + outputDuration + (videoEndOffset || 0);

    const musicInputRef = useRef(null);
    const logoInputRef = useRef(null);
    const videoInputRef = useRef(null);
    const pendingRoleRef = useRef('Watermark');

    /* ── Audio file handling ────────────────────────────────────── */

    const handleAudioFile = (file) => {
        if (!file || !file.type.startsWith('audio/')) return;
        const url = URL.createObjectURL(file);
        const id = `track_${Date.now()}`;
        const audio = new Audio(url);
        audio.addEventListener('loadedmetadata', () => {
            addAudioTrack({ id, url, name: file.name, file, volume: 0.5, offsetSeconds: 0, _audioDuration: audio.duration, pinVolume: false });
        }, { once: true });
        audio.addEventListener('error', () => {
            addAudioTrack({ id, url, name: file.name, file, volume: 0.5, offsetSeconds: 0, pinVolume: false });
        }, { once: true });
    };

    const onAudioDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        handleAudioFile(file);
    };

    /* ── Logo file handling ─────────────────────────────────────── */

    const handleLogoFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const url = URL.createObjectURL(file);
        const role = pendingRoleRef.current;
        const offset = videoStartOffset || 0;
        const outDur = outputDuration;
        const endOff = videoEndOffset || 0;
        let startTime, endTime, position;
        if (role === 'Intro') {
            startTime = 0;
            endTime = offset > 0 ? offset : outDur;
            position = 'center';
        } else if (role === 'Outro') {
            startTime = offset + outDur;
            endTime = offset + outDur + (endOff > 0 ? endOff : 5);
            position = 'center';
        } else {
            startTime = offset;
            endTime = offset + outDur;
            position = 'bottom-right';
        }
        addLogoTrack({
            url, name: file.name, file,
            position, opacity: 1, scale: 0.2,
            startTime, endTime,
            labelRole: role,
        });
    };

    const handleOutroVideoFile = (file) => {
        if (!file || !file.type.startsWith('video/')) return;
        const url = URL.createObjectURL(file);
        const role = 'Outro';
        const offset = videoStartOffset || 0;
        const outDur = outputDuration;
        const endOff = videoEndOffset || 0;
        const startTime = offset + outDur;
        const videoEl = document.createElement('video');
        videoEl.preload = 'metadata';
        videoEl.src = url;
        videoEl.addEventListener('loadedmetadata', () => {
            const dur = videoEl.duration || Math.max(1, endOff);
            const endTime = startTime + (endOff > 0 ? endOff : Math.min(dur, 5));
            addLogoTrack({
                url, name: file.name, file,
                position: 'center', opacity: 1, scale: 1.0,
                startTime, endTime,
                labelRole: role,
                isVideo: true,
            });
        }, { once: true });
        videoEl.addEventListener('error', () => {
            const endTime = startTime + Math.max(1, endOff);
            addLogoTrack({
                url, name: file.name, file,
                position: 'center', opacity: 1, scale: 1.0,
                startTime, endTime,
                labelRole: role,
                isVideo: true,
            });
        }, { once: true });
    };

    const snapTiming = (lt) => {
        const offset = videoStartOffset || 0;
        const outDur = outputDuration;
        const endOff = videoEndOffset || 0;
        if (lt.labelRole === 'Intro') {
            updateLogoTrack(lt.id, { startTime: 0, endTime: Math.max(offset, 0.5) });
        } else if (lt.labelRole === 'Outro') {
            const s = offset + outDur;
            updateLogoTrack(lt.id, { startTime: s, endTime: s + Math.max(endOff, 0.5) });
        } else if (lt.labelRole === 'Watermark') {
            updateLogoTrack(lt.id, { startTime: offset, endTime: offset + outDur });
        }
    };

    const openLogoPickerAs = (role) => {
        pendingRoleRef.current = role;
        logoInputRef.current?.click();
    };

    const onLogoDrop = (e) => {
        e.preventDefault();
        pendingRoleRef.current = 'Watermark';
        const file = e.dataTransfer.files?.[0];
        handleLogoFile(file);
    };

    const $dark = darkMode;

    return (
        <PanelRoot $dark={$dark}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.25,
                    py: 0.75,
                    borderBottom: `1px solid ${$dark ? '#2a2a3a' : '#c8cce0'}`,
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, letterSpacing: 0.5, color: $dark ? '#aaa' : '#444', fontSize: 12 }}
                >
                    Editor Controls
                </Typography>
                <Tooltip title="Close panel">
                    <IconButton size="small" onClick={onClose} sx={{ color: $dark ? '#666' : '#999', padding: '2px' }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ── SUBTITLES ────────────────────────────────────── */}
            {langKeys.length > 0 && (
                <>
                    <SectionTitle $dark={$dark}>
                        <ClosedCaptionIcon sx={{ fontSize: 13 }} />
                        Subtitles
                        <Tooltip title={previewSubsOn ? 'Hide subtitles' : 'Show subtitles'}>
                            <IconButton
                                size="small"
                                onClick={toggleSubs}
                                sx={{ ml: 'auto', color: previewSubsOn ? '#64b5f6' : ($dark ? '#444' : '#ccc'), padding: '2px' }}
                            >
                                {previewSubsOn
                                    ? <ClosedCaptionIcon sx={{ fontSize: 14 }} />
                                    : <ClosedCaptionDisabledIcon sx={{ fontSize: 14 }} />}
                            </IconButton>
                        </Tooltip>
                    </SectionTitle>
                    <Box sx={{ mx: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                            Preview language
                        </Typography>
                        <Select
                            size="small"
                            fullWidth
                            value={previewLang || langKeys[0] || ''}
                            onChange={(e) => setPreviewLang(e.target.value)}
                            variant="outlined"
                            sx={{ fontSize: 11, color: $dark ? '#ccc' : '#333', background: $dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
                        >
                            {langKeys.map((l) => (
                                <MenuItem key={l} value={l} sx={{ fontSize: 11 }}>{l}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box sx={{ mx: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
                            Timeline tracks
                        </Typography>
                        {langKeys.map((l) => {
                            const hidden = hiddenTimelineLangs.has(l);
                            return (
                                <Box
                                    key={l}
                                    onClick={() => toggleTimelineLang(l)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                        py: 0.4, px: 0.75, borderRadius: 1, cursor: 'pointer',
                                        opacity: hidden ? 0.45 : 1,
                                        '&:hover': { background: $dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                                    }}
                                >
                                    {hidden
                                        ? <VisibilityOffIcon sx={{ fontSize: 13, color: $dark ? '#555' : '#bbb', flexShrink: 0 }} />
                                        : <VisibilityIcon sx={{ fontSize: 13, color: '#64b5f6', flexShrink: 0 }} />}
                                    <Typography variant="caption" sx={{ fontSize: 11, color: $dark ? (hidden ? '#555' : '#ccc') : (hidden ? '#bbb' : '#333') }}>
                                        {l}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                    <Divider sx={{ mb: 1, borderColor: $dark ? '#2a2a3a' : '#dde' }} />
                </>
            )}

            {/* ── AUDIO TRACKS ─────────────────────────────────── */}
            <SectionTitle $dark={$dark}>
                <AudiotrackIcon sx={{ fontSize: 13 }} />
                Audio Tracks
                {audioTracks.length > 0 && (
                    <Tooltip title={`Normalize all unpinned tracks to a balanced volume (${audioTracks.filter(t => !t.pinVolume).length} tracks affected)`}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoFixHighIcon sx={{ fontSize: 12 }} />}
                            onClick={normalizeAudio}
                            sx={{
                                ml: 'auto',
                                fontSize: 10,
                                py: 0,
                                px: 0.75,
                                minWidth: 0,
                                height: 20,
                                borderColor: $dark ? '#444' : '#bbb',
                                color: $dark ? '#aaa' : '#555',
                                textTransform: 'none',
                                '&:hover': { borderColor: '#64b5f6', color: '#64b5f6' },
                            }}
                        >
                            Normalize
                        </Button>
                    </Tooltip>
                )}
            </SectionTitle>

            {audioTracks.map((track) => (
                <TrackCard key={track.id} $dark={$dark} style={track.pinVolume ? { border: '1px solid #f9a825', background: $dark ? 'rgba(249,168,37,0.07)' : 'rgba(249,168,37,0.08)' } : {}}>
                    <TrackRow>
                        <AudiotrackIcon sx={{ fontSize: 14, color: '#64b5f6', flexShrink: 0 }} />
                        <Typography
                            variant="caption"
                            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: $dark ? '#ddd' : '#333', fontSize: 11 }}
                            title={track.name}
                        >
                            {track.name}
                        </Typography>
                        <Tooltip title={track.pinVolume ? 'Pinned: excluded from normalization. Click to unpin.' : 'Pin volume: keep this track unchanged when normalizing'}>
                            <IconButton
                                size="small"
                                onClick={() => pinAudioVolume(track.id)}
                                sx={{ color: track.pinVolume ? '#f9a825' : ($dark ? '#444' : '#ccc'), padding: '2px' }}
                            >
                                <PushPinIcon sx={{ fontSize: 13, transform: track.pinVolume ? 'none' : 'rotate(45deg)', transition: 'transform 0.15s' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove track">
                            <IconButton size="small" onClick={() => removeAudioTrack(track.id)} sx={{ color: '#e57373', padding: '2px' }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </TrackRow>
                    {track.pinVolume && (
                        <TrackRow>
                            <Chip
                                label="Pinned — excluded from normalize"
                                size="small"
                                sx={{ fontSize: 9, height: 16, background: 'rgba(249,168,37,0.2)', color: '#f9a825', border: '1px solid #f9a825', borderRadius: 1 }}
                            />
                        </TrackRow>
                    )}

                    {/* Volume */}
                    <TrackRow>
                        <VolumeUpIcon sx={{ fontSize: 13, color: $dark ? '#888' : '#666', flexShrink: 0 }} />
                        <Slider
                            size="small"
                            min={0}
                            max={1}
                            step={0.05}
                            value={track.volume}
                            onChange={(_, v) => updateAudioTrack(track.id, { volume: v })}
                            sx={{ flex: 1, color: '#64b5f6' }}
                        />
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 28, textAlign: 'right' }}>
                            {Math.round(track.volume * 100)}%
                        </Typography>
                    </TrackRow>

                    {/* Start offset */}
                    <TrackRow>
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 48 }}>
                            Start at
                        </Typography>
                        <Slider
                            size="small"
                            min={0}
                            max={Math.max(duration, 1)}
                            step={0.5}
                            value={track.offsetSeconds}
                            onChange={(_, v) => updateAudioTrack(track.id, { offsetSeconds: v })}
                            sx={{ flex: 1, color: '#81c784' }}
                        />
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 32, textAlign: 'right' }}>
                            {track.offsetSeconds.toFixed(1)}s
                        </Typography>
                    </TrackRow>
                </TrackCard>
            ))}

            <DropZone
                $dark={$dark}
                onClick={() => musicInputRef.current?.click()}
                onDrop={onAudioDrop}
                onDragOver={(e) => e.preventDefault()}
            >
                <UploadFileIcon sx={{ fontSize: 22, color: $dark ? '#555' : '#aaa' }} />
                <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 10, textAlign: 'center' }}>
                    {audioTracks.length > 0 ? 'Add another audio track' : 'Drop an audio file here'}<br />or click to browse
                </Typography>
                <Typography variant="caption" sx={{ color: $dark ? '#444' : '#bbb', fontSize: 9 }}>
                    MP3 · WAV · AAC · OGG
                </Typography>
            </DropZone>
            <input
                ref={musicInputRef}
                type="file"
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={(e) => handleAudioFile(e.target.files?.[0])}
            />

            <Divider sx={{ my: 1, borderColor: $dark ? '#2a2a3a' : '#dde' }} />

            {/* ── IMAGE OVERLAYS ────────────────────────────────── */}
            <SectionTitle $dark={$dark}>
                <ImageIcon sx={{ fontSize: 13 }} />
                Image Overlays
            </SectionTitle>

            {/* Intro / Outro duration controls */}
            <TrackCard $dark={$dark} style={{ margin: '4px 8px' }}>
                <TrackRow>
                    <Typography variant="caption" sx={{ color: $dark ? '#aaa' : '#555', fontSize: 10, fontWeight: 700, minWidth: 46 }}>
                        Intro
                    </Typography>
                    <Slider
                        size="small"
                        min={0}
                        max={30}
                        step={0.5}
                        value={videoStartOffset || 0}
                        onChange={(_, v) => setVideoOffset(v)}
                        sx={{ flex: 1, color: '#ce93d8' }}
                    />
                    <Typography variant="caption" sx={{ color: '#ce93d8', fontSize: 11, ml: 0.75, fontWeight: 700, minWidth: 32, textAlign: 'right' }}>
                        {(videoStartOffset || 0).toFixed(1)}s
                    </Typography>
                </TrackRow>
                <TrackRow>
                    <Typography variant="caption" sx={{ color: $dark ? '#aaa' : '#555', fontSize: 10, fontWeight: 700, minWidth: 46 }}>
                        Outro
                    </Typography>
                    <Slider
                        size="small"
                        min={0}
                        max={30}
                        step={0.5}
                        value={videoEndOffset || 0}
                        onChange={(_, v) => setVideoEndOffset(v)}
                        sx={{ flex: 1, color: '#80cbc4' }}
                    />
                    <Typography variant="caption" sx={{ color: '#80cbc4', fontSize: 11, ml: 0.75, fontWeight: 700, minWidth: 32, textAlign: 'right' }}>
                        {(videoEndOffset || 0).toFixed(1)}s
                    </Typography>
                </TrackRow>
                <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 9, lineHeight: 1.3 }}>
                    Intro/outro durations shift the video on the timeline.
                </Typography>
            </TrackCard>

            {logoTracks.map((lt) => (
                <TrackCard key={lt.id} $dark={$dark}>
                    <TrackRow>
                        {/* Preview thumbnail */}
                        <img
                            src={lt.url}
                            alt={lt.name}
                            style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 3, background: '#000', flexShrink: 0 }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: $dark ? '#ddd' : '#333', fontSize: 11 }}
                            title={lt.name}
                        >
                            {lt.name}
                        </Typography>
                        <Tooltip title="Remove image">
                            <IconButton size="small" onClick={() => removeLogoTrack(lt.id)} sx={{ color: '#e57373', padding: '2px' }}>
                                <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </TrackRow>

                    {/* Role */}
                    <TrackRow>
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                            Role
                        </Typography>
                        <Select
                            size="small"
                            value={lt.labelRole || 'Watermark'}
                            onChange={(e) => updateLogoTrack(lt.id, { labelRole: e.target.value })}
                            variant="standard"
                            disableUnderline
                            sx={{ fontSize: 11, color: $dark ? '#ccc' : '#333', flex: 1 }}
                        >
                            <MenuItem value="Intro" sx={{ fontSize: 11 }}>Intro</MenuItem>
                            <MenuItem value="Outro" sx={{ fontSize: 11 }}>Outro</MenuItem>
                            <MenuItem value="Watermark" sx={{ fontSize: 11 }}>Watermark</MenuItem>
                            <MenuItem value="Custom" sx={{ fontSize: 11 }}>Custom</MenuItem>
                        </Select>
                    </TrackRow>

                    {/* Position */}
                    <TrackRow>
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                            Position
                        </Typography>
                        <Select
                            size="small"
                            value={lt.position}
                            onChange={(e) => updateLogoTrack(lt.id, { position: e.target.value })}
                            variant="standard"
                            disableUnderline
                            sx={{ fontSize: 11, color: $dark ? '#ccc' : '#333', flex: 1 }}
                        >
                            <MenuItem value="top-left" sx={{ fontSize: 11 }}>Top Left</MenuItem>
                            <MenuItem value="top-right" sx={{ fontSize: 11 }}>Top Right</MenuItem>
                            <MenuItem value="bottom-left" sx={{ fontSize: 11 }}>Bottom Left</MenuItem>
                            <MenuItem value="bottom-right" sx={{ fontSize: 11 }}>Bottom Right</MenuItem>
                            <MenuItem value="center" sx={{ fontSize: 11 }}>Center</MenuItem>
                        </Select>
                    </TrackRow>

                    {/* Opacity */}
                    <TrackRow>
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                            Opacity
                        </Typography>
                        <Slider
                            size="small"
                            min={0}
                            max={1}
                            step={0.05}
                            value={lt.opacity}
                            onChange={(_, v) => updateLogoTrack(lt.id, { opacity: v })}
                            sx={{ flex: 1, color: '#ce93d8' }}
                        />
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 28, textAlign: 'right' }}>
                            {Math.round(lt.opacity * 100)}%
                        </Typography>
                    </TrackRow>

                    {/* Size */}
                    <TrackRow>
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                            Size
                        </Typography>
                        <Slider
                            size="small"
                            min={0.05}
                            max={0.8}
                            step={0.05}
                            value={lt.scale ?? 0.2}
                            onChange={(_, v) => updateLogoTrack(lt.id, { scale: v })}
                            sx={{ flex: 1, color: '#80cbc4' }}
                        />
                        <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 28, textAlign: 'right' }}>
                            {Math.round((lt.scale ?? 0.2) * 100)}%
                        </Typography>
                    </TrackRow>

                    {/* Show from / Hide after — always shown for full manual control */}
                    {(lt.labelRole === 'Custom' || !lt.labelRole) ? (
                        <>
                            <TrackRow>
                                <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                                    Show from
                                </Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={Math.max(totalDuration, 1)}
                                    step={0.5}
                                    value={lt.startTime}
                                    onChange={(_, v) => updateLogoTrack(lt.id, { startTime: Math.min(v, lt.endTime - 0.5) })}
                                    sx={{ flex: 1, color: '#ffcc80' }}
                                />
                                <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 32, textAlign: 'right' }}>
                                    {lt.startTime.toFixed(1)}s
                                </Typography>
                            </TrackRow>
                            <TrackRow>
                                <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 52 }}>
                                    Hide after
                                </Typography>
                                <Slider
                                    size="small"
                                    min={0}
                                    max={Math.max(totalDuration, 1)}
                                    step={0.5}
                                    value={lt.endTime}
                                    onChange={(_, v) => updateLogoTrack(lt.id, { endTime: Math.max(v, lt.startTime + 0.5) })}
                                    sx={{ flex: 1, color: '#ffcc80' }}
                                />
                                <Typography variant="caption" sx={{ color: $dark ? '#888' : '#666', fontSize: 10, minWidth: 32, textAlign: 'right' }}>
                                    {lt.endTime.toFixed(1)}s
                                </Typography>
                            </TrackRow>
                        </>
                    ) : (
                        <TrackRow>
                            <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 10, flex: 1 }}>
                                {lt.startTime.toFixed(1)}s – {lt.endTime.toFixed(1)}s
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => snapTiming(lt)}
                                sx={{
                                    fontSize: 9, py: 0, px: 0.75, minWidth: 0, height: 20,
                                    borderColor: $dark ? '#444' : '#bbb',
                                    color: $dark ? '#aaa' : '#555',
                                    textTransform: 'none',
                                    '&:hover': { borderColor: '#64b5f6', color: '#64b5f6' },
                                }}
                            >
                                Snap timing
                            </Button>
                        </TrackRow>
                    )}
                </TrackCard>
            ))}

            {/* ── Add image buttons (vertical) ───────────────── */}
            {[
                { role: 'Intro',      label: 'Add Intro image',  Icon: FirstPageIcon,          color: '#ce93d8' },
                { role: 'Watermark',  label: 'Add Watermark',    Icon: BrandingWatermarkIcon,  color: '#64b5f6' },
                { role: 'Outro',      label: 'Add Outro image',  Icon: LastPageIcon,           color: '#80cbc4' },
            ].map(({ role, label, Icon, color }) => (
                <Box
                    key={role}
                    onClick={() => openLogoPickerAs(role)}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        mx: 1, mb: 0.5, px: 1.25, py: 0.75,
                        borderRadius: 1, cursor: 'pointer',
                        border: `1px solid ${$dark ? '#2a2a3a' : '#dde'}`,
                        transition: 'border-color 0.15s, background 0.15s',
                        '&:hover': { borderColor: color, background: `${color}18` },
                    }}
                >
                    <Icon sx={{ fontSize: 14, color, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontSize: 10, color: $dark ? '#aaa' : '#555' }}>
                        {label}
                    </Typography>
                </Box>
            ))}
            {/* Add Outro video button */}
            <Box
                onClick={() => videoInputRef.current?.click()}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    mx: 1, mb: 0.5, px: 1.25, py: 0.75,
                    borderRadius: 1, cursor: 'pointer',
                    border: `1px solid ${$dark ? '#2a2a3a' : '#dde'}`,
                    transition: 'border-color 0.15s, background 0.15s',
                    '&:hover': { borderColor: '#80cbc4', background: '#80cbc418' },
                }}
            >
                <LastPageIcon sx={{ fontSize: 14, color: '#80cbc4', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontSize: 10, color: $dark ? '#aaa' : '#555' }}>
                    Add Outro video
                </Typography>
            </Box>
            <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={(e) => { handleOutroVideoFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <DropZone
                $dark={$dark}
                onDrop={onLogoDrop}
                onDragOver={(e) => e.preventDefault()}
                style={{ cursor: 'default' }}
            >
                <UploadFileIcon sx={{ fontSize: 22, color: $dark ? '#555' : '#aaa' }} />
                <Typography variant="caption" sx={{ color: $dark ? '#666' : '#999', fontSize: 10, textAlign: 'center' }}>
                    Or drop an image here
                </Typography>
                <Typography variant="caption" sx={{ color: $dark ? '#444' : '#bbb', fontSize: 9 }}>
                    PNG · JPG · SVG · WebP
                </Typography>
            </DropZone>
            <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ''; }}
            />

            <Box sx={{ flex: 1 }} />
        </PanelRoot>
    );
}
